import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getReportStateColor, getReportTypeColor } from '../../../../constants/colors';
import { useReportsQuery } from '../../../../hooks/queries/useReportsQuery';
import { Report } from '../../../../services/apiSLTP/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { createTimeColumn, formatDate } from '../../../../utils/dates';
import { getReportTypeInfos } from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import StateCell from '../../../cells/StateCell';
import EditReportModal from '../../../modals/operations/validation/EditReportValidationModal';
import GenericAgGridTable from '../../GenericAgGridTable';
import { getReportStateLabel } from '../../../../constants/labels';
import IconButton from '../../../shared/IconButton';
import { reportStateEnum } from '../../../../constants';

interface ReportsValidationTableProps {
    currentDate: Date;
}

const ReportsValidationTable: React.FC<ReportsValidationTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const { data: dataReports, isLoading: isReportsLoading, refetch } = useReportsQuery({ queries: { date: currentDate.toISOString() } });

    const handleEdit = (report: Report) => {
        setSelectedReport(report);
        setIsEditModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsEditModalOpen(false);
        setSelectedReport(null);
    };

    const columnDefs: ColDef<Report>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            valueGetter: (params) => formatName(params.data?.userId.firstName, params.data?.userId.lastName),
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('state')),
            valueGetter: (params) => capitalizeFirstLetter(t(getReportTypeInfos(params.data?.reportType))),
            cellRenderer: (params: ICellRendererParams<Report>) => {
                if (!params.data) return params.value;
                return (
                    <StateCell
                        stateColor={getReportTypeColor(params.data?.reportType, 'background')}
                        text={capitalizeFirstLetter(t(getReportTypeInfos(params.data?.reportType)))}
                    />
                );
            },
            colId: 'state',
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(getReportStateLabel(params.data?.reportState)),
            cellRenderer: (params: ICellRendererParams<Report>) => {
                if (!params.data) return params.value;
                return (
                    <StateCell
                        stateColor={getReportStateColor(params.data?.reportState, 'background')}
                        text={capitalizeFirstLetter(getReportStateLabel(params.data?.reportState))}
                    />
                );
            },
            colId: 'status',
        },
        createTimeColumn('workStartTime', capitalizeFirstLetter(t('declaredStartTime'))),
        createTimeColumn('workEndTime', capitalizeFirstLetter(t('declaredEndTime'))),
        {
            headerName: capitalizeFirstLetter(t('extraHours')),
            field: 'extraHours',
            valueFormatter: (params) => (params.value ? formatNumber(params.value, t('locale')) : '-'),
            colId: 'extraHours',
        },
        {
            headerName: capitalizeFirstLetter(t('longTrip')),
            field: 'isLongTrip',
            valueFormatter: (params) => capitalizeFirstLetter(t(params.data?.isLongTrip ? 'yes' : 'no')),
            colId: 'longTrip',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            colId: 'actions',
            sortable: false,
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            initialWidth: 150,
            cellRenderer: (params: ICellRendererParams<Report>) => {
                if (!params.data) return null;
                if (params.data.reportState > reportStateEnum.REPORTFILLED) return null;
                return <IconButton iconName="edit" onClick={() => handleEdit(params.data!)} />;
            },
        },
    ];

    const filteredSortedData =
        dataReports
            ?.filter((report) => !report.userId.isSpecial)
            ?.sort((a, b) => formatName(a.userId.firstName, a.userId.lastName).localeCompare(formatName(b.userId.firstName, b.userId.lastName))) ||
        [];

    return (
        <>
            <GenericAgGridTable
                rowData={filteredSortedData}
                columnDefs={columnDefs}
                loading={isReportsLoading}
                exportCsvFileName={`validation-absences-${formatDate(currentDate)}`}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
            <EditReportModal open={isEditModalOpen} handleClose={handleCloseModal} report={selectedReport} refetch={refetch} />
        </>
    );
};

export default ReportsValidationTable;
